import { useNavigate } from '@tanstack/react-router'
import { Button, List, Paper, Stack, Text, ThemeIcon, Title } from '@mantine/core'
import {
  IconArrowRight,
  IconBulb,
  IconCheck,
  IconDeviceFloppy,
  IconLock,
  IconMessageCircle,
} from '@tabler/icons-react'
import { usePostHog } from '@/lib/posthog'
import { trackSaveCtaClicked } from '@/features/diy/utils/analytics'

interface ProjectGuideConversionCTAProps {
  slug: string
  isCustomized: boolean
  /** Set session intent so the post-auth return auto-claims (Story 2.4). */
  onBeforeAuthNavigation?: () => void
}

export function ProjectGuideConversionCTA({
  slug,
  isCustomized,
  onBeforeAuthNavigation,
}: ProjectGuideConversionCTAProps) {
  const navigate = useNavigate()
  const posthog = usePostHog()

  function handleSignUp() {
    trackSaveCtaClicked(posthog, {
      projectSlug: slug,
      isCustomized,
      source: 'conversion_section',
    })
    onBeforeAuthNavigation?.()
    navigate({
      to: '/sign-up',
      search: {
        redirect_url: `/diy/${slug}`,
      },
    })
  }

  function handleSignIn() {
    trackSaveCtaClicked(posthog, {
      projectSlug: slug,
      isCustomized,
      source: 'conversion_section',
    })
    onBeforeAuthNavigation?.()
    navigate({
      to: '/sign-in',
      search: {
        redirect_url: `/diy/${slug}`,
      },
    })
  }

  return (
    <Paper
      p="xl"
      withBorder
      radius="md"
      bg="light-dark(var(--mantine-color-primary-0), var(--mantine-color-dark-7))"
    >
      <Stack gap="lg" align="center">
        <ThemeIcon size="xl" variant="light" color="primary" radius="xl">
          <IconLock size={24} />
        </ThemeIcon>

        <Title order={3} ta="center">
          Save & Track Your Progress
        </Title>

        <Text size="sm" c="dimmed" ta="center" maw={400}>
          Create a free account to save this personalized plan and unlock all features.
        </Text>

        <List
          spacing="sm"
          size="sm"
          center
          icon={
            <ThemeIcon color="green" size="sm" radius="xl">
              <IconCheck size={12} />
            </ThemeIcon>
          }
        >
          <List.Item icon={
            <ThemeIcon color="primary" size="sm" radius="xl">
              <IconDeviceFloppy size={12} />
            </ThemeIcon>
          }>
            Save this personalized plan
          </List.Item>
          <List.Item>
            Track your progress step-by-step
          </List.Item>
          <List.Item icon={
            <ThemeIcon color="violet" size="sm" radius="xl">
              <IconMessageCircle size={12} />
            </ThemeIcon>
          }>
            Get AI help when you're stuck
          </List.Item>
          <List.Item icon={
            <ThemeIcon color="yellow" size="sm" radius="xl">
              <IconBulb size={12} />
            </ThemeIcon>
          }>
            Access pro tips and video guides
          </List.Item>
        </List>

        <Stack gap="xs" w="100%" maw={300}>
          <Button
            size="lg"
            rightSection={<IconArrowRight size={18} />}
            onClick={handleSignUp}
            fullWidth
          >
            Sign Up Free — No CC Required
          </Button>

          <Text size="xs" c="dimmed" ta="center">
            Already have an account?{' '}
            <Text
              component="button"
              size="xs"
              c="primary"
              td="underline"
              style={{ cursor: 'pointer', background: 'none', border: 'none' }}
              onClick={handleSignIn}
            >
              Sign in
            </Text>
          </Text>
        </Stack>
      </Stack>
    </Paper>
  )
}
